import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { Box, Stack, Tab, Tabs } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

const TABS = [
  { key: "insumos", label: "Insumos", path: "/stock/base-de-datos/insumos" },
  { key: "elaborados", label: "Elaborados", path: "/stock/base-de-datos/elaborados" },
  {
    key: "productos",
    label: "Productos de venta",
    path: "/stock/base-de-datos/productos-de-venta",
  },
];

const BaseDatosListStateContext = createContext(null);

function activeTab(pathname) {
  if (pathname.includes("/elaborados")) return "elaborados";
  if (pathname.includes("/productos-de-venta") || pathname.includes("/recetas")) {
    return "productos";
  }
  return "insumos";
}

export function useBaseDatosListState(key, initialState) {
  const context = useContext(BaseDatosListStateContext);
  const cached = context?.getState(key);
  const state = cached ?? initialState;
  const remember = useCallback(
    (nextState) => context?.setState(key, nextState),
    [context, key]
  );
  return [state, remember];
}

export default function BaseDatosLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const stateRef = useRef({});
  const lastListPathRef = useRef({
    insumos: TABS[0].path,
    elaborados: TABS[1].path,
    productos: TABS[2].path,
  });

  const selected = activeTab(location.pathname);
  const selectedRoot = TABS.find((tab) => tab.key === selected)?.path;
  if (location.pathname === selectedRoot) {
    lastListPathRef.current[selected] = `${location.pathname}${location.search}`;
  }

  const contextValue = useMemo(
    () => ({
      getState: (key) => stateRef.current[key],
      setState: (key, value) => {
        stateRef.current[key] = value;
      },
    }),
    []
  );

  return (
    <BaseDatosListStateContext.Provider value={contextValue}>
      <Stack sx={{ width: "100%", minWidth: 0, minHeight: 0, flex: 1 }}>
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, flexShrink: 0 }}>
          <Tabs
            value={selected}
            onChange={(_, key) =>
              navigate(
                lastListPathRef.current[key] ||
                  TABS.find((tab) => tab.key === key)?.path
              )
            }
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Apartados de Base de datos"
            sx={{
              minHeight: 44,
              bgcolor: "neutral.100",
              borderRadius: "22px",
              px: 0.75,
              boxShadow: "inset 0 0 3px rgba(96,96,113,.28)",
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                minHeight: 36,
                my: 0.5,
                borderRadius: "18px",
                color: "primary.900",
                fontSize: 13,
                fontWeight: 800,
                textTransform: "none",
              },
              "& .MuiTab-root.Mui-selected": {
                bgcolor: "primary.500",
                color: "#fff !important",
              },
            }}
          >
            {TABS.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ minHeight: 0, flex: 1, display: "flex", flexDirection: "column" }}>
          <Outlet />
        </Box>
      </Stack>
    </BaseDatosListStateContext.Provider>
  );
}
